import { Volume } from '@components/AudioPlayer/components/Volume';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import DownloadJob from '@components/icons/DownloadJob.svg';
import { buildSafeAudioFilename } from '@helpers/buildSafeAudioFilename';
import { downloadFromUrl } from '@helpers/downloadFromUrl';
import { handleDownloadError } from '@helpers/jobLinkHelpers';
import { ElementRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Controls } from './components/Controls';
import { ProgressBar } from './components/ProgressBar';
import { TProps } from './types/TProps';

export const audioPlayerRootClassName = [
  'audio-player flex h-8 w-full min-w-0 max-w-full flex-nowrap items-center justify-center gap-3 pb-2.5',
  '[&_.audio]:hidden',
  '[&_.controls]:shrink-0',
  '[&_.volume]:h-[22px] [&_.volume]:shrink-0',
  '[&_.download-button]:ml-2 [&_.download-button]:shrink-0 [&_.download-button]:cursor-pointer [&_.download-button]:text-[var(--color-text-muted)] [&_.download-button:hover]:text-[var(--color-title)]',
].join(' ');

export const audioPlayerProgressRangeClassName = [
  'relative h-1 min-w-0 w-full max-w-[400px] cursor-pointer appearance-none rounded border-0',
  '[&::-moz-range-track]:relative [&::-moz-range-track]:h-1 [&::-moz-range-track]:w-full [&::-moz-range-track]:cursor-pointer [&::-moz-range-track]:rounded [&::-moz-range-track]:border-0 [&::-moz-range-track]:bg-[var(--color-border)]',
  '[&::-webkit-slider-runnable-track]:relative [&::-webkit-slider-runnable-track]:h-1 [&::-webkit-slider-runnable-track]:w-full [&::-webkit-slider-runnable-track]:cursor-pointer [&::-webkit-slider-runnable-track]:rounded [&::-webkit-slider-runnable-track]:border-0 [&::-webkit-slider-runnable-track]:bg-[var(--color-border)]',
  '[&::before]:absolute [&::before]:left-0 [&::before]:top-0 [&::before]:z-[1] [&::before]:h-1 [&::before]:rounded [&::before]:bg-[var(--color-text-muted)] [&::before]:content-[""]',
  '[&::-moz-range-progress]:h-1 [&::-moz-range-progress]:rounded [&::-moz-range-progress]:bg-[var(--color-text-muted)]',
  '[&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:z-10 [&::-webkit-slider-thumb]:-mt-[7px] [&::-webkit-slider-thumb]:h-[18px] [&::-webkit-slider-thumb]:w-[18px] [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-[3px] [&::-webkit-slider-thumb]:border-solid [&::-webkit-slider-thumb]:border-surface [&::-webkit-slider-thumb]:bg-[var(--color-text-muted)] [&::-webkit-slider-thumb]:shadow-[0_0_6px_0_var(--color-shadow)] [&::-webkit-slider-thumb]:transition-all [&::-webkit-slider-thumb]:duration-[50ms]',
  '[&::-moz-range-thumb]:relative [&::-moz-range-thumb]:z-10 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-[3px] [&::-moz-range-thumb]:border-solid [&::-moz-range-thumb]:border-surface [&::-moz-range-thumb]:bg-[var(--color-text-muted)] [&::-moz-range-thumb]:shadow-[0_0_6px_0_var(--color-shadow)] [&::-moz-range-thumb]:transition-all [&::-moz-range-thumb]:duration-[50ms]',
].join(' ');

export interface AudioPlayerHandle {
  seekTo: (time: number) => void;
}

export const AudioPlayer = forwardRef<AudioPlayerHandle, TProps & { className?: string }>(
  ({ className = 'audio-player', src, onPlay, onError, onReady, onDownload, downloadFileName }, ref) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [timeProgress, setTimeProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const audioRef = useRef<ElementRef<'audio'>>(null);
    const progressBarRef = useRef<ElementRef<'input'>>(null);
    const isDownloadingRef = useRef(false);

    const seekTo = (time: number) => {
      if (audioRef.current) {
        audioRef.current.currentTime = time;
        if (progressBarRef.current) {
          progressBarRef.current.value = time.toString();
          progressBarRef.current.style.setProperty('--range-progress', `${(time / duration) * 100}%`);
        }
        setTimeProgress(time);
      }
    };

    useImperativeHandle(ref, () => ({
      seekTo,
    }));

    useEffect(() => {
      if (!audioRef.current) {
        return;
      }
      if (isPlaying) {
        audioRef.current.play();
      } else {
        audioRef.current.pause();
      }
    }, [isPlaying]);

    useEffect(() => {
      const audioEl = audioRef.current;
      if (!audioEl) {
        return;
      }
      const handleTimeUpdate = () => {
        const currentTime = audioEl.currentTime;
        setTimeProgress(currentTime);
        if (progressBarRef.current) {
          progressBarRef.current.value = currentTime.toString();
          progressBarRef.current.style.setProperty('--range-progress', `${(currentTime / duration) * 100}%`);
        }
        if (onPlay) {
          onPlay(currentTime);
        }
      };

      audioEl.addEventListener('timeupdate', handleTimeUpdate);
      return () => {
        audioEl.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }, [duration, onPlay]);

    const download = async () => {
      if (isDownloadingRef.current) {
        return;
      }
      if (!src) {
        handleDownloadError(new Error('No source URL provided for download.'));
        return;
      }
      const fileName = downloadFileName || buildSafeAudioFilename(src);
      isDownloadingRef.current = true;
      setIsDownloading(true);
      try {
        if (onDownload) {
          await onDownload();
        } else {
          await downloadFromUrl(src, fileName);
        }
      } catch (error) {
        handleDownloadError(error);
      } finally {
        isDownloadingRef.current = false;
        setIsDownloading(false);
      }
    };

    const onLoadedMetadata = () => {
      if (!audioRef.current) {
        return;
      }
      const seconds = audioRef.current.duration;
      setDuration(seconds);
      onReady?.();
    };

    if (!src) {
      return null;
    }

    return (
      <div className={`${audioPlayerRootClassName} ${className}`}>
        <audio
          className="audio"
          src={src}
          ref={audioRef}
          preload="metadata"
          onError={onError}
          onLoadedMetadata={onLoadedMetadata}
          onSeeking={() => setIsLoading(true)}
          onSeeked={() => setIsLoading(false)}
          onWaiting={() => setIsLoading(true)}
          onEnded={() => {
            setIsPlaying(false);
          }}
        />
        <Controls
          disabled={!progressBarRef.current || !duration}
          isPlaying={isPlaying}
          isLoading={isLoading}
          setIsPlaying={setIsPlaying}
        />
        <ProgressBar progressBarRef={progressBarRef} seekTo={seekTo} timeProgress={timeProgress} duration={duration} />
        <Volume audioRef={audioRef} />
        {isDownloading ? (
          <div
            className="ml-2 flex h-6 w-6 shrink-0 items-center justify-center text-[var(--color-accent)]"
            role="status"
            aria-live="polite"
            aria-label="Downloading audio"
          >
            <svg
              className="animate-audioPlayerDownloadRotation"
              viewBox="0 0 24 24"
              width="24"
              height="24"
              focusable="false"
              aria-hidden="true"
            >
              <circle
                className="stroke-current [stroke-linecap:round]"
                cx="12"
                cy="12"
                r="9"
                fill="none"
                strokeWidth="3"
                strokeDasharray="45"
              />
            </svg>
          </div>
        ) : (
          <IconButtonClean type="button" title="Download audio" className="download-button" onClick={download}>
            <DownloadJob />
          </IconButtonClean>
        )}
      </div>
    );
  },
);

AudioPlayer.displayName = 'AudioPlayer';
